/**
 * 调色板的兜底值 + 读取器
 *
 * **颜色的唯一来源是 `src/global.scss` 的 `:root`（`--muou-*`）**，这里不含「另一套配色」，
 * 只放两样东西：
 *
 * 1. **读取器**：antd 的颜色 token 必须是可解析的颜色值——它要拿这些值现算 hover、浅底、
 *    禁用态，直接传 `var(--muou-primary)` 会在启动时抛错（实测 2026-10-23 白屏）。
 *    所以这里是「读我们自己的变量，再把具体值交给 antd」。
 * 2. **兜底值**：`getComputedStyle` 读不到自定义属性时用（jsdom 测试环境、CSS 尚未就绪）。
 *    改配色请改 `global.scss`，兜底值跟着改，别把它当第二份调色板用。
 *
 * 取值见 muou-app-doc `design/99-公共（common）/99-51-…` 第 4.1 节。
 */
export const THEME_FALLBACK = {
  primary: '#2563eb',
  primaryHover: '#1d4ed8',
  primaryActive: '#1b45c4',
  primarySoft: '#eef4ff',
  primaryBorder: '#dce7ff',
  bgLayout: '#f3f5f9',
  surface: '#ffffff',
  border: '#d8e0eb',
  split: '#e7ecf3',
  text: '#1e293b',
  textBody: '#334155',
  textSecondary: '#526176',
  textMuted: '#64748b',
  buttonDisabledBg: '#edf1f6',
  buttonDisabledText: '#94a3b8',
  tableHead: '#f0f2f5',
  rowHover: '#f4f8fb',
} as const;

/** 调色板的运行时取值：读 `:root` 的自定义属性，读不到就回落兜底值 */
export function readThemePalette() {
  const read = (name: string, fallback: string) => {
    if (typeof window === 'undefined') return fallback;
    const value = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
    return value || fallback;
  };

  return {
    primary: read('--muou-primary', THEME_FALLBACK.primary),
    primaryHover: read('--muou-primary-hover', THEME_FALLBACK.primaryHover),
    primaryActive: read('--muou-primary-active', THEME_FALLBACK.primaryActive),
    primarySoft: read('--muou-primary-soft', THEME_FALLBACK.primarySoft),
    primaryBorder: read('--muou-primary-border', THEME_FALLBACK.primaryBorder),
    bgLayout: read('--muou-bg-layout', THEME_FALLBACK.bgLayout),
    surface: read('--muou-surface', THEME_FALLBACK.surface),
    border: read('--muou-border', THEME_FALLBACK.border),
    split: read('--muou-split', THEME_FALLBACK.split),
    text: read('--muou-text', THEME_FALLBACK.text),
    textBody: read('--muou-text-body', THEME_FALLBACK.textBody),
    textSecondary: read('--muou-text-secondary', THEME_FALLBACK.textSecondary),
    textMuted: read('--muou-text-muted', THEME_FALLBACK.textMuted),
    buttonDisabledBg: read('--muou-button-disabled-bg', THEME_FALLBACK.buttonDisabledBg),
    buttonDisabledText: read('--muou-button-disabled-text', THEME_FALLBACK.buttonDisabledText),
    tableHead: read('--muou-table-head', THEME_FALLBACK.tableHead),
    rowHover: read('--muou-row-hover', THEME_FALLBACK.rowHover),
  };
}
